// TODO: Move all t1 article related API calls here
import type { ArticleDetailsModel, ArticleDropReasonModel, ArticleForecastModel, ArticleHistoryModel, ArticleModel, ArticleSegmentationModel, ArticleStateModel, AssignedModelListItem, GetArticleFilterValuesModel, GetArticleStreamModel, GetCatalogArticlesTreeModel, ModelArticleModel, ModelStatsModel, ModelsSkuCountModel, SeasonsDataModel, UpdateArticleForecastQuantityModel, UpdateArticlePricesModel, UpdateArticleSegmentationsModel, UpdateArticlesLockModel, UpdateArticlesSegmentationsBulkItem, UpdateArticlesStatusModel, UpdateParentModelInfoModel, ValidateArticlesModel } from './model/articleModel'
import type { LinkedCatalogArticleModel } from './model/LinkedCatalogArticleModel'
import type { T1JobModel } from './model/jobModel'
import Net from '@/services/net'
import DuneAsset from '@/models/duneAsset'
import utils from '@/services/utils'
import { backgroundJobConstants } from '@/models/constants'

/**
 * Get list of articles
 * @param catalogCode
 * @param filter filter expression (ArticleNumber|123456-001:ModelNumber|123456)
 * @param u toISOString date
 * @param attr comma separated attribute system names
 * @returns ArticleModel[]
 */
export function getArticles(catalogCode: number, filter?: string, u?: string, attr?: string, dataSourceTypeId?: number) {
  const params = new URLSearchParams()
  if (utils.isDefined(filter)) {
    params.set('filter', filter)
  }
  if (utils.isDefined(u)) {
    params.set('u', u)
  }
  if (utils.isDefined(attr)) {
    params.set('attr', attr)
  }
  let url = `/catalogs/${catalogCode}/articles`
  if (dataSourceTypeId === 3) {
    url = `/catalogs/${catalogCode}/assortablearticles`
  }
  return Net.t1.get<ArticleModel[]>(`${url}?${params.toString()}`)
}

export function getArticlesStream(catalogCode: number, params?: { u?: string, articleId?: number, modelId?: number, status?: boolean, firstArticle?: boolean, topResult?: number, body?: GetArticleStreamModel }, signal?: AbortSignal) {
  const query = new URLSearchParams()
  if (utils.isDefined(params?.u)) {
    query.set('u', params.u)
  }
  if (utils.isDefined(params?.articleId)) {
    query.set('articleId', params.articleId.toString())
  }
  if (utils.isDefined(params?.modelId)) {
    query.set('modelId', params.modelId.toString())
  }
  if (utils.isDefined(params?.status)) {
    query.set('status', params.status.toString())
  }
  if (utils.isDefined(params?.firstArticle)) {
    query.set('firstArticle', params.firstArticle.toString())
  }
  if (utils.isDefined(params?.topResult)) {
    query.set('topResult', params.topResult.toString())
  }
  return Net.t1v2.post<ArticleModel[]>(`/catalogs/${catalogCode}/articles/stream?${query.toString()}`, params?.body || {}, signal)
}

export function getArticlesCount(catalogCode: number, params?: { u?: string, articleId?: number, modelId?: number, status?: boolean, firstArticle?: boolean, body?: GetArticleStreamModel }, signal?: AbortSignal) {
  const query = new URLSearchParams()
  if (utils.isDefined(params?.u)) {
    query.set('u', params.u)
  }
  if (utils.isDefined(params?.articleId)) {
    query.set('articleId', params.articleId.toString())
  }
  if (utils.isDefined(params?.modelId)) {
    query.set('modelId', params.modelId.toString())
  }
  if (utils.isDefined(params?.status)) {
    query.set('status', params.status.toString())
  }
  if (utils.isDefined(params?.firstArticle)) {
    query.set('firstArticle', params.firstArticle.toString())
  }
  return Net.t1v2.post<number>(`/catalogs/${catalogCode}/articles/count?${query.toString()}`, params?.body || {}, signal)
}

export function getCatalogArticlesTree(catalogCode: number, treeId: number, customerId?: number) {
  const query = new URLSearchParams()
  if (utils.isDefined(customerId) && customerId > 0) {
    query.set('customerId', customerId.toString())
  }
  return Net.t1v2.get<GetCatalogArticlesTreeModel[]>(`/catalogs/${catalogCode}/articles/trees/${treeId}?${query.toString()}`)
}

export function getAttributeUniqueValues(catalogCode: number, attributeSystemName: string) {
  return Net.t1v2.get<string[]>(`/catalogs/${catalogCode}/attributes/${attributeSystemName}/values`)
}

export function getArticleFilterValues(catalogCode: number, payload: GetArticleFilterValuesModel, limit?: number, isUnique?: boolean) {
  const query = new URLSearchParams()
  if (utils.isDefined(limit)) {
    query.set('limit', limit.toString())
  }
  if (utils.isDefined(isUnique)) {
    query.set('isUnique', isUnique.toString())
  }
  return Net.t1v2.post<Record<string, any>[]>(`/catalogs/${catalogCode}/filterarticles?${query.toString()}`, payload)
}

export function validateCatalogArticles(catalogCode: number, payload: ValidateArticlesModel, customerId?: number) {
  const query = new URLSearchParams()
  if (utils.isDefined(customerId) && customerId > 0) {
    query.set('customerId', customerId.toString())
  }
  return Net.t1v2.post<{ ValidArticlesCount: number, InvalidArticleIds: number[] }>(`/catalogs/${catalogCode}/validatearticles?${query.toString()}`, payload)
}

export function getModelsSkuCount(catalogCode: number, payload: ModelsSkuCountModel) {
  return Net.t1v2.post<Record<string, Record<number, number>>>(`/catalogs/${catalogCode}/models/sku/count`, payload)
}

export function getModelArticles(sellerAccountId: number, attributeGroupId: number, modelId: number, catalogCodes: number[], articleId?: number) {
  const params = new URLSearchParams()
  params.set('catalogs', catalogCodes.join(','))
  if (utils.isDefined(articleId)) {
    params.set('articleid', articleId.toString())
  }
  return Net.t1.get<ModelArticleModel[]>(`/sellers/${sellerAccountId}/attributegroups/${attributeGroupId}/models/${modelId}/articles?${params.toString()}`)
}

export function getLinkedCatalogArticles(catalogCode: number, modelId: number, linkedCatalogCodeList: number[]) {
  const params = new URLSearchParams()
  params.set('linkedcatalogs', linkedCatalogCodeList.join(','))
  return Net.t1.get<LinkedCatalogArticleModel[]>(`/catalogs/${catalogCode}/models/${modelId}/articles?${params.toString()}`)
}

export function getArticleHistory(catalogCode: number, articleId: number) {
  return Net.t1.get<ArticleHistoryModel[]>(`/catalogs/${catalogCode}/articles/${articleId}/history`)
}

export function createArticle(catalogCode: number, requestObject: Record<string, any>, createModelFlag: number, requestId?: number, shouldConfirmRequest = false) {
  const params = new URLSearchParams()
  if (createModelFlag === 1) {
    params.set('createmodel', '1')
  }
  else if (createModelFlag === 2) {
    params.set('createchildmodel', '1')
  }
  else {
    params.set('createmodel', '0')
  }
  if (requestId) {
    params.set('requestid', requestId.toString())
  }
  if (shouldConfirmRequest) {
    params.set('confirmrequest', 'true')
  }
  return Net.t1.post(`/catalogs/${catalogCode}/models/articles?${params.toString()}`, requestObject)
}

export function assignArticle(catalogCode: number, requestObject: Record<string, any>, isCreateChildModel: boolean, modelId: number, requestId?: number, shouldConfirmRequest = false) {
  const params = new URLSearchParams()
  if (isCreateChildModel) {
    params.set('createchildmodel', '1')
    if (utils.isDefined(modelId)) {
      params.set('NewModelId', modelId.toString())
    }
  }
  if (requestId) {
    params.set('requestid', requestId.toString())
  }
  if (shouldConfirmRequest) {
    params.set('confirmrequest', 'true')
  }
  return Net.t1.post(`/catalogs/${catalogCode}/models/assign/articles?${params.toString()}`, requestObject)
}

export function updateArticle(catalogCode: number, articleId: number, requestObject: Record<string, any>, updateModelLevelAttributes = false, requestId: number | null = null) {
  let url = `/catalogs/${catalogCode}/articles/${articleId}?clientapp=t1sweb&updateModelLevelAttributes=${updateModelLevelAttributes}`
  if (requestId !== null) {
    url = `${url}&requestid=${requestId}`
  }
  return Net.t1.put(url, requestObject)
}

export function updateArticlesStatus(catalogCode: number, requestObject: UpdateArticlesStatusModel[], requestId: number | null = null) {
  let url = `/catalogs/${catalogCode}/articles/status`
  if (requestId !== null) {
    url = `${url}?requestid=${requestId}`
  }
  return Net.t1.put(url, requestObject)
}

export function updateArticlesLock(catalogCode: number, isLock: boolean, requestObject: UpdateArticlesLockModel[]) {
  if (isLock) {
    return Net.t1.put(`/catalogs/${catalogCode}/articles/lock`, requestObject)
  }
  else {
    return Net.t1.put(`/catalogs/${catalogCode}/articles/unlock`, requestObject)
  }
}

export function updateArticleShipmentWindowRange(catalogCode: number, articleId: number, retailWindowId: number | '') {
  // '' will remove the shipment window from article
  return Net.t1.put(`/catalogs/${catalogCode}/articles/${articleId}/shipmentwindowrange`, { ShipmentWindowRangeId: retailWindowId })
}

export function updateArticleSegmentations(catalogCode: number, articleId: number, requestObject: UpdateArticleSegmentationsModel[]) {
  return Net.t1.put<ArticleSegmentationModel[]>(`/catalogs/${catalogCode}/articles/${articleId}/articlesegmentations`, requestObject)
}

export function updateArticlesSegmentationsBulk(catalogCode: number, requestObject: UpdateArticlesSegmentationsBulkItem[]) {
  return Net.t1.put<T1JobModel>(`/catalogs/${catalogCode}/articlesSegmentations?sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.editArticleSegmentation}`, requestObject)
}

export function getArticleForecasts(catalogCode: number, customerId: number, articleId: number) {
  return Net.t1.get<ArticleForecastModel[]>(`/catalogs/${catalogCode}/forecasts/article?customerid=${customerId}&articleid=${articleId}`)
}

export function updateArticleForecastQuantity(catalogCode: number, articleId: number, requestObject: UpdateArticleForecastQuantityModel[]) {
  return Net.t1.put(`/catalogs/${catalogCode}/forecasts/articles/${articleId}`, requestObject)
}

export function getArticleDropReasons(sellerId: number) {
  return Net.t1.get<ArticleDropReasonModel[]>(`/sellers/${sellerId}/articles/reasons`)
}

export function getArticleNumberToBeAssigned(catalogCode: number, colorId: number, modelId: number, articleId: number) {
  return Net.t1.get(`/catalogs/${catalogCode}/colors/${colorId}?modelid=${modelId}&articleid=${articleId}`)
}

export function updateArticlePrices(catalogCode: number, recordId: number | string, payload: UpdateArticlePricesModel, isPriceByStyleApply: number, requestId: number | null = null) {
  let url = `catalogs/${catalogCode}/articles/${recordId}/articleprices`

  if (isPriceByStyleApply) {
    url = `catalogs/${catalogCode}/modelnumber/${recordId}/prices`
  }

  if (requestId !== null) {
    url = `${url}?requestid=${requestId}`
  }
  return Net.t1.put(url, payload)
}

export function getArticleStates(sellerAccountId: number, attributeGroupId: number) {
  return Net.t1.get<ArticleStateModel[]>(`sellers/${sellerAccountId}/attributegroups/${attributeGroupId}/articlestates`)
    .then((response) => {
      // sort States by StateRank
      const sortedData = response.data.sort((a, b) => a.StateRank - b.StateRank) as Array<ArticleStateModel>
      return sortedData
    })
}

export function getModelStats(catalogCode: number, modelId: number) {
  return Net.t1.get<ModelStatsModel[]>(`/catalogs/${catalogCode}/models/${modelId}/stats`)
}

export function updateArticlesState(catalogCode: number, articles, stateId: number | null, requestId: number | null = null) {
  const payload = articles.reduce((acu, cur) => (acu.push({ ArticleId: cur.Id, StateId: stateId })) && acu, [])
  let url = `catalogs/${catalogCode}/articles/articlestates`
  if (requestId !== null) {
    url = `${url}?requestid=${requestId}`
  }
  return Net.t1.put(url, payload)
}

export function updateParentModelInfo(sellerId: number, attributeGroupId: number, payload: UpdateParentModelInfoModel[]) {
  return Net.t1.put(`sellers/${sellerId}/${attributeGroupId}/modifyparentmodelinfo`, payload)
}

export function updateModelSeasonlessAttributes(sellerId: number, attributeGroupId: number, modelId: number, requestObject: Record<string, any>) {
  const url = `/sellers/${sellerId}/${attributeGroupId}/models/${modelId}?clientapp=t1sweb`
  return Net.t1.put(url, requestObject)
}

export function getAssignedCatalogModels(catalogCode: number) {
  return Net.t1.get<AssignedModelListItem[]>(`/catalogs/${catalogCode}/true/models/assigned`)
}

export function updateModelAttributes(catalogCode: number, modelId: number, requestObject: Record<string, any>) {
  const url = `/catalogs/${catalogCode}/true/models/${modelId}?clientapp=t1sweb`
  return Net.t1.put(url, requestObject)
}

export function getArticleAssets(context: string, contextKey: string, imageSet: string, newestImageAssetKeyList: string[] = [], firstImage: boolean = false, signal?: AbortSignal): Promise<DuneAsset[]> {
  return Net.dunes.get<DuneAsset[]>(`/assets`, { params: { Context: context, ContextKey: contextKey, ImageSet: imageSet, Status: true, FirstImage: firstImage }, signal })
    .then((response) => {
      let assets: Array<DuneAsset> = []
      if (response && response.data) {
        response.data.forEach((res) => {
          assets.push(new DuneAsset(context, res))
        })
        assets = utils.sortAssets(assets, new Set(newestImageAssetKeyList.map(x => x.toLowerCase())))
      }
      return assets
    })
    .catch((_e) => {
      return []
    })
}

export function getArticlesAssets(context: string, contextKey: string, imageSets: string[], firstImage: boolean = false) {
  return Net.dunes.post<DuneAsset[]>(`/assets/q`, { Context: context, ContextKey: contextKey, Op: 'equals', Filter: imageSets, Status: true, FirstImage: firstImage, Type: 'ImageSet' })
}

export function articlesBgJob(catalogCode: number, requestObject: Record<string, any>, isCarryover = false) {
  const params = new URLSearchParams()
  params.set('sourceapplication', backgroundJobConstants.sourceApplication)
  if (isCarryover) {
    params.set('sourceaction', backgroundJobConstants.sourceAction.carryoverArticles)
    params.set('carryover', '1')
  }
  else {
    params.set('sourceaction', backgroundJobConstants.sourceAction.copyModel)
  }
  return Net.t1.put(`/catalogs/${catalogCode}/articles?${params.toString()}`, requestObject)
}

export function assignArticleColors(catalogCode: number, articleId: number, requestObject: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/articles/${articleId}/assignArticleColors`, requestObject)
}

export function assortArticlesBgJob(catalogCode: number, requestArticles: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/articles?sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.assortArticles}`, requestArticles)
}

export function updateViaImportBgJob(catalogCode: number, requestArticles: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/updatearticles?sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.assortArticles}`, requestArticles)
}

export function updateViaImportModelBgJob(catalogCode: number, requestArticles: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/importbymodels?sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.assortArticles}`, requestArticles)
}

export function importByLicenseeBgJob(catalogCode: number, requestArticles: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/articles?importbylicensee=true&sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.assortArticles}`, requestArticles)
}

export function setSizesByLicenseeModelNumberBgJob(catalogCode: number, requestData: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/licenseemodels/assignsizescales?sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.assortArticles}`, requestData)
}

export function setPricesByLicenseeModelNumberBgJob(catalogCode: number, requestData: Record<string, any>) {
  return Net.t1.put(`/catalogs/${catalogCode}/licenseemodels/assignprices?sourceapplication=${backgroundJobConstants.sourceApplication}&sourceaction=${backgroundJobConstants.sourceAction.assortArticles}`, requestData)
}

export function getModelPrices(catalogCode: number, modelId: number) {
  return Net.t1.get(`/catalogs/${catalogCode}/models/${modelId}/prices`)
}

export function getSeasonsData(articleId: number) {
  return Net.t1.get<SeasonsDataModel[]>(`/catalogs/articles/${articleId}/seasondata`)
}

export function getArticleDetails(catalogCode: number, articleId: number) {
  return Net.t1.get<ArticleDetailsModel>(`/catalogs/${catalogCode}/articles/${articleId}`)
}

export function getAvailableCarryoverArticles(catalogCode: number, payload: Record<string, string[]>) {
  return Net.t1.post<any[]>(`/catalogs/${catalogCode}/availablecarryoverarticles`, payload)
}
